import {
  useAddFullProductKitToCartMutation,
  useAddProductKitToCartMutation,
  useAddProductVariantToWholesaleCartMutation,
  useCartUpdateProductVariantMutation,
} from '@graphql/mutations/__generated__/product-reflection.generated'
import { useDeleteCartLineMutation } from '@graphql/mutations/__generated__/cart.generated'
import { ProductCardVariantQuery } from '@graphql/queries/__generated__/product-card.generated'
import { useVariantKitDataLazyQuery } from '@graphql/queries/__generated__/product-variant-kit.generated'
import { ApolloCache } from '@apollo/client'
import AddShoppingCartIcon from '@icons/AddShoppingCartIcon'
import BoxIcon from '@icons/BoxIcon'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_WHOLESALE_PRODUCT_QTY } from '../../../../constants'
import { CartTypeEnum, OutOfStockStatusEnum } from '../../../../generated/gqlTypes'
import { formErrorsToString, mutationWarningsToMessages } from '../../../misc/utils'
import { syncCartCache } from '../../../utils/cartCache'
import KitQuantityStepper from '../VariantKitPanel/KitQuantityStepper'
import VariantKitPanel, { ISupplyFormData } from '../VariantKitPanel/VariantKitPanel'
import { ExtraRow, LinkButton, multiplyPriceRange, Price, PriceRange, SubmitButton } from './common'

type Variant = ProductCardVariantQuery['productVariant']

interface Props {
  variant: Variant
  isArchive: boolean
}

const singlePriceRange = (currency: string, discount: number, undiscount = discount): PriceRange => ({
  currency,
  discount: { min: discount, max: discount },
  undiscount: { min: undiscount, max: undiscount },
})

// Sets the variant's wholesale cart state in the cache (the fields the form reads), leaving the retail ones alone.
const setWholesaleCartState = (cache: ApolloCache<unknown>, variantId: string, quantity: number, lines?: number[]) => {
  const isWholesaleField = (storeFieldName: string) => storeFieldName.includes(CartTypeEnum.Wholesale)
  cache.modify({
    id: cache.identify({ __typename: 'ProductVariantType', id: variantId }),
    fields: {
      cartQuantity: (value, { storeFieldName }) => (isWholesaleField(storeFieldName) ? quantity : value),
      cartLines: (value, { storeFieldName }) => (isWholesaleField(storeFieldName) && lines ? lines : value),
    },
  })
}

// Form of the product card panel in wholesale mode. The wholesale cart keeps one line per variant, so the button
// sets that line's quantity (add, then update), as the old wholesale list did, and the extra row can remove it.
// A kit works as in retail mode (KitSelection): the saved default kit ("Add Default KIT to Cart"), "Customize the
// KIT" for a customizable one, customizing only when there's no saved default; at wholesale prices.
const WholesaleSelection: React.FC<Props> = ({ variant, isArchive }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const cartQuantity = variant.cartQuantity || 0
  const cartLinePk = variant.cartLines?.[0] ?? null
  const [quantity, setQuantity] = useState(cartQuantity || 1)
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [kitFormData, setKitFormData] = useState<ISupplyFormData | null>(null)
  const [isKitPanelOpen, setIsKitPanelOpen] = useState(false)
  const [kitSubmitting, setKitSubmitting] = useState(false)

  // The returned variants carry their new `cartQuantity(cartType: WHOLESALE)`, which updates this form.
  const [addVariantToCart] = useAddProductVariantToWholesaleCartMutation({
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => syncCartCache(cache, data?.addVariantToCart.cart),
  })
  const [addFullKitToCart] = useAddFullProductKitToCartMutation({
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => syncCartCache(cache, data?.addFullKitToCart.cart),
  })
  // Saving a default changes `hasDefaultKit` and its price, so the variant is refetched.
  const [addKitToCart] = useAddProductKitToCartMutation({
    refetchQueries: ['CartLines', 'ProductCardVariant'],
    update: (cache, { data }) => {
      if (!data?.addKitToCart.errors.length) syncCartCache(cache, data?.addKitToCart.cart)
    },
  })
  // A kit already in the cart only changes its quantity: adding it again would replace its parts.
  const [updateCartLine] = useCartUpdateProductVariantMutation({
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => {
      const result = data?.cartUpdateVariant
      if (result?.errors.length || !result?.cartLine) return
      syncCartCache(cache, result.cart)
      setWholesaleCartState(cache, variant.id, result.cartLine.quantity)
    },
  })
  const [deleteCartLine] = useDeleteCartLineMutation({
    refetchQueries: ['CartLines'],
    update: (cache, { data }) => {
      syncCartCache(cache, data?.deleteCartLine.cart)
      setWholesaleCartState(cache, variant.id, 0, [])
    },
  })
  const [loadKitData, kitDataQuery] = useVariantKitDataLazyQuery({ fetchPolicy: 'network-only' })

  const isKit = variant.isSupply
  const canCustomize = isKit && variant.isCustomizable
  // A customizable kit without a saved default can only be customized.
  const canAddWholeKit = !canCustomize || variant.hasDefaultKit
  const defaultKitPrice = canCustomize && variant.hasDefaultKit ? variant.defaultKitWholesalePrice : null
  const unitPriceRange = defaultKitPrice
    ? singlePriceRange(defaultKitPrice.currency, defaultKitPrice.gross)
    : singlePriceRange(
        variant.wholesalePrice.currency,
        variant.wholesalePrice.discount,
        variant.wholesalePrice.undiscount
      )
  const isAvailable = variant.stockStatus !== OutOfStockStatusEnum.Out && !isArchive
  // Stock isn't tracked for sale (IN) or can be backordered: only the hard ceiling applies.
  const isUnlimited =
    variant.outOfStockStatus === OutOfStockStatusEnum.In || variant.outOfStockStatus === OutOfStockStatusEnum.Backorder
  const maxQuantity = isUnlimited
    ? MAX_WHOLESALE_PRODUCT_QTY
    : Math.min(MAX_WHOLESALE_PRODUCT_QTY, Math.max(variant.stockQuantity, 1))
  const isAdded = cartQuantity > 0 && cartQuantity === quantity
  const customizeLoading = kitDataQuery.loading
  const busy = adding || removing || customizeLoading

  const showWarnings = (warnings?: { messages?: string[] }[] | null) =>
    mutationWarningsToMessages(warnings).forEach(message => enqueueSnackbar(message, { variant: 'warning' }))

  const onAddToCart = async () => {
    setAdding(true)
    try {
      if (isKit && cartQuantity > 0 && cartLinePk) {
        const { data } = await updateCartLine({
          variables: { cartLine: cartLinePk, variant: variant.pk, quantity, cartType: CartTypeEnum.Wholesale },
        })
        const errors = data?.cartUpdateVariant.errors || []
        if (errors.length) enqueueSnackbar(formErrorsToString(errors), { variant: 'error' })
      } else if (isKit) {
        const { data } = await addFullKitToCart({
          variables: { pk: variant.pk, cartType: CartTypeEnum.Wholesale, quantity },
        })
        showWarnings(data?.addFullKitToCart.warnings)
      } else {
        const { data } = await addVariantToCart({ variables: { pk: variant.pk, quantity } })
        const errors = data?.addVariantToCart.errors || []
        if (errors.length) enqueueSnackbar(formErrorsToString(errors), { variant: 'error' })
      }
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setAdding(false)
  }

  // Opens the kit form, filled with the kit's parts in the cart if it's there.
  const onCustomize = async () => {
    const { data, error } = await loadKitData({
      variables: { pk: variant.pk, cartLine: cartLinePk, cartType: CartTypeEnum.Wholesale },
    })
    if (error || !data?.productVariant.customizeKitData) {
      enqueueSnackbar(error?.message || t('productCard.loadError', "Couldn't load product options."), {
        variant: 'error',
      })
      return
    }
    setKitFormData(data.productVariant.customizeKitData)
    setIsKitPanelOpen(true)
  }

  const onSubmitKit = async (
    supplyLine: number | null,
    variants: { pk: number; quantity: number }[],
    setAsDefault: boolean
  ) => {
    setKitSubmitting(true)
    try {
      const { data } = await addKitToCart({
        variables: {
          pk: variant.pk,
          variants: variants.map(({ pk, quantity: partQuantity }) => ({ pk, quantity: partQuantity })),
          setAsDefault,
          quantity,
          cartType: CartTypeEnum.Wholesale,
        },
      })
      const errors = data?.addKitToCart.errors || []
      if (errors.length) {
        throw Error(errors[0].messages[0])
      }
      showWarnings(data.addKitToCart.warnings)
      setIsKitPanelOpen(false)
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setKitSubmitting(false)
  }

  const onRemove = async () => {
    setRemoving(true)
    try {
      await deleteCartLine({ variables: { variant: variant.id, cartType: CartTypeEnum.Wholesale } })
      setQuantity(1)
    } catch (e) {
      enqueueSnackbar(e.message, { variant: 'error' })
    }
    setRemoving(false)
  }

  // Customizing is the main action only for a kit that can't be added as a whole yet (not in the cart either).
  const customizeIsMain = !canAddWholeKit && !cartQuantity

  const buttonLabel = () => {
    if (!isAvailable && !isArchive) return t('productOutOfStock', 'Out of Stock')
    if (customizeIsMain) return t('button.customizeTheKit', 'Customize the KIT')
    if (isAdded) return t('button.addedPcs', { defaultValue: 'Added: {{count}} pcs', count: cartQuantity })
    if (cartQuantity) return t('button.update', 'Update')
    if (!isKit) return t('button.addToCart', 'Add to Cart')
    return canCustomize
      ? t('button.addDefaultKitToCart', 'Add Default KIT to Cart')
      : t('productCard.addKitToCart', 'Add KIT to Cart')
  }

  return (
    <>
      <div className='product-card-panel__price-row product-card-panel__price-row--stepper'>
        <KitQuantityStepper quantity={quantity} availableQuantity={maxQuantity} onChangeQuantity={setQuantity} />
        <Price range={multiplyPriceRange(unitPriceRange, quantity)} />
      </div>
      <ExtraRow>
        {canCustomize && !customizeIsMain && (
          <LinkButton loading={customizeLoading} disabled={!isAvailable || busy} onClick={onCustomize}>
            {t('button.customizeTheKit', 'Customize the KIT')}
          </LinkButton>
        )}
        {cartQuantity > 0 && (
          <LinkButton loading={removing} disabled={busy} onClick={onRemove}>
            {t('button.remove', 'Remove')}
          </LinkButton>
        )}
      </ExtraRow>
      <SubmitButton
        loading={customizeIsMain ? customizeLoading : adding}
        icon={isKit ? <BoxIcon viewBox='0 -960 960 960' /> : <AddShoppingCartIcon viewBox='0 -960 960 960' />}
        disabled={!isAvailable || (!customizeIsMain && isAdded) || busy}
        onClick={customizeIsMain ? onCustomize : onAddToCart}
      >
        {buttonLabel()}
      </SubmitButton>
      {isKit && (
        <VariantKitPanel
          isOpen={isKitPanelOpen}
          onOpen={() => setIsKitPanelOpen(true)}
          onClosePanel={() => setIsKitPanelOpen(false)}
          onSubmit={onSubmitKit}
          loading={kitSubmitting}
          data={kitFormData}
          isWholesale
        />
      )}
    </>
  )
}

export default WholesaleSelection
